import { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Typography, TextField, Button, Stack,
    CircularProgress, Avatar, Chip, Fade,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import paymentdashboardservice from "../../../services/paymentdashboardservice";
import { RATING_META, GoldStarDefs, StarPicker, ReadonlyStars } from "../../common/StarRating";

const MIN_CHARS = 200;
const MAX_CHARS = 500;

function StatusPill({ rating }) {
    if (!rating) return null;
    const m = RATING_META[rating];
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.25, py: 0.4, borderRadius: 99,
            background: m.bg, border: `1.5px solid ${m.border}`,
        }}>
            <Typography sx={{ fontSize: 11, lineHeight: 1, color: m.iconColor }}>{m.icon}</Typography>
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: m.iconColor, fontFamily: 'Quicksand', lineHeight: 1 }}>
                {m.statusText}
            </Typography>
        </Box>
    );
}

function AvgBanner({ avg, total }) {
    const rounded = avg > 0 ? Math.round(avg) : 0;
    const m = rounded >= 1 ? RATING_META[rounded] : null;

    return (
        <Box sx={{
            display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' },
            flexWrap: 'wrap', gap: { xs: 2, sm: 3 },
            p: { xs: '16px 20px', sm: '22px 28px' }, borderRadius: 3,
            background: 'linear-gradient(135deg,rgba(255,255,255,0.97) 0%,rgba(248,250,255,0.93) 100%)',
            border: '1px solid rgba(0,0,0,0.07)',
            boxShadow: '0 2px 20px rgba(0,0,0,0.04)',
        }}>
            <Box sx={{ textAlign: 'center', minWidth: 76 }}>
                <Typography sx={{
                    fontSize: {
                        xs:40,
                        sm:48,
                        md:56
                    }, fontWeight: 800, lineHeight: 1,
                    fontFamily: 'Quicksand',
                    color: avg > 0 ? '#C8850E' : '#D1D5DB',
                    textShadow: avg > 0 ? '0 4px 20px rgba(210,140,10,0.28)' : 'none',
                    transition: 'color 0.3s ease, text-shadow 0.3s ease',
                }}>
                    {avg > 0 ? avg.toFixed(1) : '—'}
                </Typography>
                <Typography sx={{ fontSize: 10, color: '#9CA3AF', fontFamily: 'Quicksand', fontWeight: 600, mt: 0.25 }}>
                    out of 5
                </Typography>
            </Box>

            <Box sx={{ flex: 1 }}>
                <ReadonlyStars value={rounded} size={26} />
                <Typography sx={{ mt: 0.75, fontSize: 12.5, color: '#6B7280', fontFamily: 'Quicksand', fontWeight: 600 }}>
                    {total === 0
                        ? 'No ratings yet — be the first!'
                        : `Based on ${total} team rating${total === 1 ? '' : 's'}`}
                </Typography>
                {m && (
                    <Chip label={m.label} size="small" sx={{
                        mt: 1, height: 22, fontSize: 11, fontWeight: 700, fontFamily: 'Quicksand',
                        background: m.bg, color: m.color, border: `1px solid ${m.border}`,
                    }} />
                )}
            </Box>
        </Box>
    );
}

function TeamCard({ r, isMe, onDelete, isDeleting }) {
    const m = r.rating >= 1 ? RATING_META[r.rating] : RATING_META[3];
    const dateStr = r.updatedAt
        ? new Date(r.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' })
        : '';

    return (
        <Box sx={{
            p: '16px 20px', borderRadius: 2.5,
            background: isMe
                ? 'linear-gradient(135deg,rgba(255,82,0,0.03) 0%,rgba(255,140,0,0.015) 100%)'
                : 'rgba(255,255,255,0.78)',
            border: isMe ? '1px solid rgba(255,82,0,0.16)' : '1px solid rgba(0,0,0,0.07)',
            transition: 'box-shadow 0.2s ease',
            '&:hover': { boxShadow: '0 4px 18px rgba(0,0,0,0.07)' },
            position: 'relative',
        }}>
            {isMe && (
                <Stack direction="row" alignItems="center" spacing={0.75}
                    sx={{ position: 'absolute', top: 11, right: 12 }}>
                    <Chip label="You" size="small" sx={{
                        height: 20, fontSize: 10, fontWeight: 700, fontFamily: 'Quicksand',
                        background: 'rgba(255,82,0,0.10)', color: '#FF5200',
                    }} />
                    {isDeleting ? (
                        <CircularProgress size={11} sx={{ color: '#EF4444' }} />
                    ) : (
                        <Box
                            onClick={onDelete}
                            title="Remove your rating"
                            sx={{
                                cursor: 'pointer', fontSize: 13, lineHeight: 1,
                                color: '#9CA3AF', userSelect: 'none',
                                transition: 'color 0.15s ease',
                                '&:hover': { color: '#EF4444' },
                            }}
                        >
                            ✕
                        </Box>
                    )}
                </Stack>
            )}
            <Stack direction="row" spacing={1.75} alignItems="flex-start">
                <Avatar sx={{
                    width: 40, height: 40, fontSize: 15, fontWeight: 700,
                    background: `linear-gradient(135deg,${m.color}28,${m.color}48)`,
                    color: m.color,
                }}>
                    {r.userFullName?.charAt(0)?.toUpperCase() || '?'}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mb: 0.5, gap: 0.75 }}>
                        <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#1C1C2E', fontFamily: 'Quicksand' }}>
                            {r.userFullName}
                        </Typography>
                        <StatusPill rating={r.rating} />
                    </Stack>
                    <Stack direction="row" alignItems="center" spacing={1.25}>
                        <ReadonlyStars value={r.rating} size={15} />
                        {dateStr && (
                            <Typography sx={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'Quicksand' }}>
                                {dateStr}
                            </Typography>
                        )}
                    </Stack>
                    {r.comment && (
                        <Typography sx={{
                            mt: 1, fontSize: 13, color: '#374151', fontFamily: 'Quicksand',
                            lineHeight: 1.65, fontStyle: 'italic',
                            borderLeft: `2.5px solid ${m.color}55`, pl: 1.5,
                        }}>
                            "{r.comment}"
                        </Typography>
                    )}
                </Box>
            </Stack>
        </Box>
    );
}

export default function CreatorRemarksTab({ creatorId }) {
    const qc = useQueryClient();
    const [myRating, setMyRating] = useState(0);
    const [comment, setComment] = useState('');
    const [saved, setSaved] = useState(false);
    const [resetting, setResetting] = useState(false);
    const didInit = useRef(false);
    const cardRef = useRef(null);
    // Tracks the last successfully saved state — used for click-outside reset
    const serverRating = useRef(0);
    const serverComment = useRef('');

    const { data, isLoading } = useQuery({
        queryKey: ['influencer-ratings', creatorId],
        queryFn: () => paymentdashboardservice.getInfluencerRatings(creatorId),
        staleTime: 60_000,
        refetchOnWindowFocus: false,
    });

    useEffect(() => {
        if (data && !didInit.current) {
            didInit.current = true;
            const mine = data.ratings?.find(r => String(r.userId) === String(data.currentUserId));
            const rating = mine?.rating ?? 0;
            const text = mine?.comment || '';
            setMyRating(rating);
            setComment(text);
            serverRating.current = rating;
            serverComment.current = text;
        }
    }, [data]);

    useEffect(() => {
        if (!saved) return;
        const t = setTimeout(() => setSaved(false), 3000);
        return () => clearTimeout(t);
    }, [saved]);

    const mutation = useMutation({
        mutationFn: (payload) => paymentdashboardservice.saveInfluencerRating(creatorId, payload),
        onSuccess: (res, variables) => {
            if (res?.success) {
                qc.setQueryData(['influencer-ratings', creatorId], res);
                setSaved(true);
                serverRating.current = variables.rating;
                serverComment.current = variables.comment;
            }
        },
    });

    const deleteMutation = useMutation({
        mutationFn: () => paymentdashboardservice.deleteInfluencerRating(creatorId),
        onSuccess: (res) => {
            if (res?.success) {
                qc.setQueryData(['influencer-ratings', creatorId], res);
                setMyRating(0);
                setComment('');
                setSaved(false);
                serverRating.current = 0;
                serverComment.current = '';
            }
        },
    });

    // Smoothly revert unsaved edits when user clicks outside the rating card
    const resetToSaved = useCallback(() => {
        setResetting(true);
        setTimeout(() => {
            setMyRating(serverRating.current);
            setComment(serverComment.current);
            setResetting(false);
        }, 160);
    }, []);

    useEffect(() => {
        function handleOutsideClick(e) {
            if (!cardRef.current || cardRef.current.contains(e.target)) return;
            const hasUnsavedChanges =
                myRating !== serverRating.current || comment !== serverComment.current;
            if (hasUnsavedChanges) resetToSaved();
        }
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [myRating, comment, resetToSaved]);

    const isFlagged = myRating > 0 && myRating <= 2;
    const cc = comment.trim().length;
    const canSave = myRating > 0 && cc >= MIN_CHARS;
    const meta = myRating ? RATING_META[myRating] : null;

    const ratings = data?.ratings || [];
    const avg = data?.averageRating || 0;
    const total = data?.totalRatings || 0;
    const meId = data?.currentUserId;

    const sorted = [...ratings].sort((a, b) => {
        if (String(a.userId) === String(meId)) return -1;
        if (String(b.userId) === String(meId)) return 1;
        return new Date(b.updatedAt) - new Date(a.updatedAt);
    });

    const showError = !saved && (mutation.isError || (mutation.data && !mutation.data.success));

    if (isLoading) {
        return (
            <Stack alignItems="center" justifyContent="center" sx={{ py: 10 }}>
                <CircularProgress size={30} sx={{ color: '#FF5200' }} />
            </Stack>
        );
    }

    return (
        <Box sx={{
            maxWidth: 700, mx: 'auto', py: 1.5,
            '@keyframes fadeUp': { from: { opacity: 0, transform: 'translateY(10px)' }, to: { opacity: 1, transform: 'translateY(0)' } },
            '@keyframes slideIn': { from: { opacity: 0, transform: 'translateX(-8px)' }, to: { opacity: 1, transform: 'translateX(0)' } },
        }}>
            {/* Gold gradient defs — must be in DOM before any star SVG renders */}
            <GoldStarDefs />

            {/* Average banner */}
            <Box sx={{ mb: 3, animation: 'fadeUp 0.32s ease both' }}>
                <AvgBanner avg={avg} total={total} />
            </Box>

            {/* My rating card */}
            <Box ref={cardRef} sx={{
                mb: 3, p: { xs: '16px 18px', sm: '24px 28px' }, borderRadius: 3,
                background: 'linear-gradient(135deg,rgba(255,255,255,0.97) 0%,rgba(255,248,245,0.94) 100%)',
                border: '1px solid rgba(255,82,0,0.10)',
                boxShadow: '0 2px 18px rgba(255,82,0,0.05)',
                animation: 'fadeUp 0.36s ease 0.06s both',
                // Smooth visual feedback when unsaved edits are discarded on outside click
                opacity: resetting ? 0.55 : 1,
                transform: resetting ? 'scale(0.988) translateY(3px)' : 'scale(1) translateY(0)',
                transition: 'opacity 0.16s ease, transform 0.16s ease',
            }}>
                <Typography sx={{
                    fontSize: 10.5, fontWeight: 700, color: '#9CA3AF',
                    fontFamily: 'Quicksand', letterSpacing: 1.2, mb: 2,
                }}>
                    YOUR RATING
                </Typography>

                {/* Star picker + label row */}
                <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{ mb: 2, gap: 1.5 }}>
                    <StarPicker value={myRating} onChange={setMyRating} />
                    <Fade in={!!meta}>
                        <Stack direction="row" spacing={1} alignItems="center">
                            {meta && (
                                <>
                                    <Chip label={meta.label} size="small" sx={{
                                        height: 26, fontSize: 12, fontWeight: 700, fontFamily: 'Quicksand',
                                        background: meta.bg, color: meta.color, border: `1px solid ${meta.border}`,
                                    }} />
                                    <StatusPill rating={myRating} />
                                </>
                            )}
                        </Stack>
                    </Fade>
                </Stack>

                {/* Flag warning for low ratings */}
                {isFlagged && (
                    <Box sx={{
                        mb: 2, px: 2, py: 1.5, borderRadius: 2,
                        background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.18)',
                        display: 'flex', alignItems: 'center', gap: 1.25,
                    }}>
                        <Typography sx={{ fontSize: 17, lineHeight: 1 }}>⚑</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#DC2626', fontFamily: 'Quicksand', fontWeight: 600 }}>
                            This influencer will be flagged based on this rating.
                        </Typography>
                    </Box>
                )}

                {/* Comment textarea */}
                <TextField
                    multiline
                    rows={4}
                    fullWidth
                    placeholder="Add a detailed note (required, min. 200 characters)..."
                    value={comment}
                    onChange={e => setComment(e.target.value)}
                    inputProps={{ maxLength: MAX_CHARS }}
                    helperText={`${comment.length} / ${MAX_CHARS}${myRating > 0 && cc >= MIN_CHARS ? ' ✓' : ''}`}
                    FormHelperTextProps={{
                        sx: {
                            textAlign: 'right', fontFamily: 'Quicksand', fontSize: 11, mr: 0,
                            color: myRating > 0 && cc >= MIN_CHARS ? '#10B981' : myRating > 0 && cc > 0 ? '#F59E0B' : undefined,
                        },
                    }}
                    sx={{
                        mb: 2.5,
                        '& .MuiOutlinedInput-root': {
                            fontFamily: 'Quicksand', fontSize: 13.5, borderRadius: 2,
                            '& fieldset': { borderColor: myRating > 0 && cc < MIN_CHARS ? 'rgba(239,68,68,0.35)' : 'rgba(0,0,0,0.12)' },
                            '&:hover fieldset': { borderColor: myRating > 0 && cc < MIN_CHARS ? 'rgba(239,68,68,0.6)' : 'rgba(255,82,0,0.3)' },
                            '&.Mui-focused fieldset': { borderColor: myRating > 0 && cc < MIN_CHARS ? '#EF4444' : '#FF5200', borderWidth: 2 },
                        },
                    }}
                />

                {/* Save row */}
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Fade in={myRating > 0 && cc < MIN_CHARS}>
                        <Typography sx={{ fontSize: 12, color: '#EF4444', fontFamily: 'Quicksand', fontWeight: 600 }}>
                            A comment of at least {MIN_CHARS} characters is required
                        </Typography>
                    </Fade>
                    {!(myRating > 0 && cc < MIN_CHARS) && <Box />}

                    <Button
                        onClick={() => canSave && !mutation.isLoading && mutation.mutate({ rating: myRating, comment: comment.trim() })}
                        disabled={!canSave || mutation.isLoading}
                        variant="contained"
                        disableElevation
                        sx={{
                            height: 40, px: 3.5, borderRadius: 2,
                            fontFamily: 'Quicksand', fontWeight: 700, fontSize: 13,
                            textTransform: 'none', letterSpacing: 0.2,
                            background: canSave ? 'linear-gradient(135deg,#FF5200,#FF8000)' : undefined,
                            boxShadow: canSave ? '0 4px 14px rgba(255,82,0,0.28)' : 'none',
                            transition: 'all 0.2s ease',
                            '&:not(:disabled):hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: '0 6px 22px rgba(255,82,0,0.38)',
                            },
                        }}
                    >
                        {mutation.isLoading
                            ? <CircularProgress size={15} sx={{ color: '#fff' }} />
                            : 'Save Changes'}
                    </Button>
                </Stack>

                {/* Success toast */}
                <Fade in={saved}>
                    <Box sx={{
                        mt: 1.75, px: 2, py: 1.1, borderRadius: 1.75,
                        background: 'rgba(16,185,129,0.07)', border: '1px solid rgba(16,185,129,0.22)',
                        display: 'flex', alignItems: 'center', gap: 1,
                    }}>
                        <Typography sx={{ fontSize: 14, color: '#10B981', lineHeight: 1 }}>✓</Typography>
                        <Typography sx={{ fontSize: 12.5, color: '#059669', fontFamily: 'Quicksand', fontWeight: 600 }}>
                            Rating saved successfully
                        </Typography>
                    </Box>
                </Fade>

                {/* Error toast */}
                {showError && (
                    <Box sx={{
                        mt: 1.75, px: 2, py: 1.1, borderRadius: 1.75,
                        background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)',
                    }}>
                        <Typography sx={{ fontSize: 12.5, color: '#DC2626', fontFamily: 'Quicksand', fontWeight: 600 }}>
                            {mutation.data?.error || 'Failed to save. Please try again.'}
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Team ratings list */}
            {sorted.length > 0 && (
                <Box sx={{ animation: 'fadeUp 0.4s ease 0.1s both' }}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                        <Typography sx={{
                            fontSize: 10.5, fontWeight: 700, color: '#9CA3AF',
                            fontFamily: 'Quicksand', letterSpacing: 1.2,
                        }}>
                            TEAM RATINGS
                        </Typography>
                        <Chip label={sorted.length} size="small" sx={{
                            height: 20, fontSize: 11, fontWeight: 700, fontFamily: 'Quicksand',
                            background: 'rgba(255,82,0,0.08)', color: '#FF5200',
                        }} />
                    </Stack>
                    <Stack spacing={1.5}>
                        {sorted.map((r, i) => {
                            const isMe = String(r.userId) === String(meId);
                            return (
                                <Box key={r._id || i} sx={{ animation: `slideIn 0.3s ease ${0.05 * i}s both` }}>
                                    <TeamCard
                                        r={r}
                                        isMe={isMe}
                                        onDelete={isMe ? () => deleteMutation.mutate() : undefined}
                                        isDeleting={isMe && deleteMutation.isLoading}
                                    />
                                </Box>
                            );
                        })}
                    </Stack>
                </Box>
            )}

            {sorted.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 6 }}>
                    <Typography sx={{ fontSize: 44, lineHeight: 1, mb: 1.5 }}>☆</Typography>
                    <Typography sx={{ fontSize: 14, fontFamily: 'Quicksand', fontWeight: 700, color: '#374151' }}>
                        No ratings yet
                    </Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#9CA3AF', fontFamily: 'Quicksand', mt: 0.5 }}>
                        Rate this creator to help your team make better decisions.
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
